import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import TranslationProvider from '../../providers/TranslationProvider';
import { sanitizeEmailInput } from '../../utils/emailValidation';

import './Input.scss';

const Input = ({
  ariaAutocomplete,
  ariaControls,
  ariaLabel,
  ariaRequired,
  ariaDescribedby,
  ariaInvalid,
  autoFocus,
  ariaLive,
  className,
  name,
  type,
  placeholder,
  disabled,
  autocomplete,
  onBlur,
  onFocus,
  onKeyDown,
  inputMode,
  pattern,
  maxlength,
  reference,
  onPaste,
  sanitizeValue,
  errors,
  dangerouslySetInnerHTML: { __html: translation },
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    // update parent state on mount
    onChange({ [name]: inputValue });
  }, []);

  useEffect(() => {
    // if we manually updated value prop from a parent, simulate a change
    if (value !== inputValue) {
      setInputValue(value);
      onChange({ [name]: value });
    }
  }, [value]);

  const handleChange = (e) => {
    const newValue = sanitizeValue?.(e.target.value) ?? e.target.value;
    setInputValue(newValue);
    onChange({ [name]: newValue });
  };

  const handleBeforeInput = type === 'email' ? (e) => {
    // Without this, the default behavior of an input type="email" will allow the
    // user to enter whitespace in an email address, which we want to aggressively
    // prevent. We cannot detect this in the handleChange event because HTML5
    // specs around type="email" behavior don't consider trailing space to
    // be a "change".
    //
    // See, e.g.,
    // https://stackoverflow.com/questions/44613550/html-why-does-the-input-tag-with-type-email-is-not-including-number-of-spaces
    // for more explanation.
    if (typeof e.data === 'string' && /\s/.test(e.data)) {
      e.preventDefault();
    }
  } : undefined;

  const invalid = errors && errors.indexOf(name) !== -1;
  const inputProps = { ...(autoFocus && { autoFocus }) };

  return (
    <input
      aria-label={ariaLabel}
      aria-required={ariaRequired}
      aria-describedby={ariaDescribedby}
      aria-invalid={ariaInvalid}
      aria-live={ariaLive}
      aria-controls={ariaControls}
      aria-autocomplete={ariaAutocomplete}
      className={cx('Input', className, {
        'Input--error': invalid,
      })}
      id={name}
      name={name}
      onBlur={onBlur}
      onBeforeInput={handleBeforeInput}
      onChange={handleChange}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      type={type}
      inputMode={inputMode}
      pattern={pattern}
      placeholder={translation || placeholder}
      disabled={disabled}
      value={(inputValue) ? `${inputValue}` : ''}
      autoComplete={autocomplete}
      maxLength={maxlength}
      ref={reference}
      onPaste={onPaste}
      {...inputProps}
    />
  );
};

Input.propTypes = {
  ariaAutocomplete: PropTypes.string,
  ariaControls: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaRequired: PropTypes.bool,
  ariaDescribedby: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  autoFocus: PropTypes.bool,
  ariaLive: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  inputMode: PropTypes.string,
  pattern: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  value: PropTypes.string,
  autocomplete: PropTypes.string,
  maxlength: PropTypes.string,
  reference: PropTypes.func,
  onPaste: PropTypes.func,
  sanitizeValue: PropTypes.func,
  errors: PropTypes.arrayOf(PropTypes.string),
  dangerouslySetInnerHTML: PropTypes.shape({
    __html: PropTypes.string,
  }),
};

Input.defaultProps = {
  ariaAutocomplete: undefined,
  ariaControls: undefined,
  ariaLabel: undefined,
  ariaRequired: false,
  ariaDescribedby: undefined,
  ariaInvalid: undefined,
  autoFocus: undefined,
  ariaLive: undefined,
  className: undefined,
  type: 'text',
  onBlur: null,
  onFocus: null,
  onKeyDown: undefined,
  inputMode: undefined,
  pattern: undefined,
  placeholder: null,
  disabled: false,
  value: '',
  autocomplete: undefined,
  maxlength: undefined,
  reference: undefined,
  onPaste: undefined,
  sanitizeValue: undefined,
  errors: [],
  dangerouslySetInnerHTML: {
    __html: '',
  },
};

// wrappers for types and google autofill
const TextInput = (props) => (<Input {...props} type="text" />);
TextInput.displayName = 'TextInput';
const EmailInput = (props) => (
  <TranslationProvider
    translationKey="email_address"
  >
    <Input
      {...props}
      name="email"
      type="email"
      autocomplete="email"
      sanitizeValue={sanitizeEmailInput}
      placeholder="Email Address"
    />
  </TranslationProvider>
);
EmailInput.displayName = 'EmailInput';
const FirstNameInput = (props) => (
  <TranslationProvider
    translationKey="first_name"
  >
    <Input {...props} name="first_name" type="text" autocomplete="given-name" placeholder="First Name" />
  </TranslationProvider>
);
FirstNameInput.displayName = 'FirstNameInput';
const LastNameInput = (props) => (
  <TranslationProvider
    translationKey="last_name"
  >
    <Input {...props} name="last_name" type="text" autocomplete="family-name" placeholder="Last Name" />
  </TranslationProvider>
);
LastNameInput.displayName = 'LastNameInput';
const Address1Input = (props) => (
  <TranslationProvider
    translationKey="address_form.placeholders.address1"
  >
    <Input {...props} placeholder="Street Address" />
  </TranslationProvider>
);
Address1Input.displayName = 'Address1Input';
const Address2Input = (props) => (
  <TranslationProvider
    translationKey="address_form.placeholders.address2"
  >
    <Input {...props} placeholder="Apartment, suite, etc. (optional)" />
  </TranslationProvider>
);
Address2Input.displayName = 'Address2Input';
const CityInput = (props) => (
  <TranslationProvider
    translationKey="address_form.placeholders.city"
  >
    <Input {...props} placeholder="City" />
  </TranslationProvider>
);
CityInput.displayName = 'CityInput';
const StateInput = (props) => (
  <TranslationProvider
    translationKey="address_form.placeholders.state"
  >
    <Input {...props} placeholder="State" />
  </TranslationProvider>
);
StateInput.displayName = 'Stateinput';
const PostalCodeInput = (props) => (
  <TranslationProvider
    translationKey="address_form.placeholders.postal_code"
  >
    <Input {...props} placeholder="Postal Code" />
  </TranslationProvider>
);
PostalCodeInput.displayName = 'PostalCodeInput';

export {
  FirstNameInput,
  LastNameInput,
  TextInput,
  EmailInput,
  Address1Input,
  Address2Input,
  CityInput,
  StateInput,
  PostalCodeInput,
};

Input.displayName = 'Input';
export default Input;
